import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, Save, X, LogOut, Ticket, TrendingUp } from 'lucide-react';
import { adminGetCoupons, adminCreateCoupon, adminUpdateCoupon, adminDeleteCoupon, adminGetCouponUsage, adminMe, adminLogout } from '../../lib/api';
import toast from 'react-hot-toast';

const emptyForm = { code: '', tipo: 'porcentagem', valor: 10, minCompra: 0, maxUsos: 100, ativo: true, expiraEm: '' };

export default function AdminCouponsPage() {
  const [coupons, setCoupons] = useState([]);
  const [usage, setUsage] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ ...emptyForm });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('coupons');
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      await adminMe();
      const [cRes, uRes] = await Promise.all([adminGetCoupons(), adminGetCouponUsage()]);
      setCoupons(cRes.data || []);
      setUsage(uRes.data || []);
    } catch (e) { navigate('/admin/login', { replace: true }); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const startEdit = (c) => {
    setEditingId(c.id);
    setFormData({ ...c, expiraEm: c.expiraEm ? c.expiraEm.slice(0, 16) : '' });
    setShowAddForm(false);
  };

  const cancelEdit = () => { setEditingId(null); setFormData({ ...emptyForm }); };

  const handleSave = async () => {
    try {
      const data = { ...formData };
      if (data.expiraEm) data.expiraEm = new Date(data.expiraEm).toISOString();
      else data.expiraEm = null;
      data.code = data.code.trim().toUpperCase();
      if (editingId) { await adminUpdateCoupon(editingId, data); toast.success('Atualizado!'); }
      else { await adminCreateCoupon(data); toast.success('Criado!'); }
      cancelEdit(); setShowAddForm(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir cupom?')) return;
    try { await adminDeleteCoupon(id); toast.success('Excluído!'); load(); } catch(e) { toast.error('Erro'); }
  };

  const handleLogout = async () => { try { await adminLogout(); } catch(e) {} localStorage.removeItem('motoveloz-admin-token'); navigate('/admin/login'); };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <header className="bg-zinc-900 border-b border-zinc-800 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2"><img src="/logo.png" alt="" className="w-10 h-10 object-contain" /><span className="text-white font-bold uppercase text-lg" style={{ fontFamily: 'Teko, sans-serif' }}>Admin</span></Link>
            <nav className="hidden md:flex gap-4">
              <Link to="/admin/dashboard" className="text-zinc-400 hover:text-white font-bold text-sm uppercase tracking-wider">Dashboard</Link>
              <Link to="/admin/produtos" className="text-zinc-400 hover:text-white font-bold text-sm uppercase tracking-wider">Produtos</Link>
              <Link to="/admin/cupons" className="text-red-500 font-bold text-sm uppercase tracking-wider">Cupons</Link>
            </nav>
          </div>
          <button onClick={handleLogout} className="text-zinc-400 hover:text-red-500 flex items-center gap-2 text-sm"><LogOut className="h-4 w-4" /> Sair</button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold text-white uppercase tracking-tight" style={{ fontFamily: 'Teko, sans-serif' }} data-testid="admin-coupons-title">Cupons</h1>
          <button onClick={() => { setShowAddForm(!showAddForm); setEditingId(null); setFormData({ ...emptyForm }); }} data-testid="add-coupon-btn"
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 font-bold text-sm uppercase flex items-center gap-2"><Plus className="h-4 w-4" /> Novo Cupom</button>
        </div>

        <div className="flex gap-2 mb-6">
          <button onClick={() => setTab('coupons')} className={`px-4 py-2 font-bold text-sm uppercase tracking-wider ${tab === 'coupons' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="tab-coupons">
            <Ticket className="h-4 w-4 inline mr-1" /> Cupons ({coupons.length})
          </button>
          <button onClick={() => setTab('usage')} className={`px-4 py-2 font-bold text-sm uppercase tracking-wider ${tab === 'usage' ? 'bg-red-600 text-white' : 'bg-zinc-800 text-zinc-400'}`} data-testid="tab-usage">
            <TrendingUp className="h-4 w-4 inline mr-1" /> Uso ({usage.length})
          </button>
        </div>

        {showAddForm && (
          <div className="bg-zinc-900 border-2 border-green-600 p-6 mb-6">
            <h2 className="text-xl font-bold text-white mb-4">Novo Cupom</h2>
            <CouponForm formData={formData} setFormData={setFormData} onSave={handleSave} onCancel={() => { setShowAddForm(false); cancelEdit(); }} />
          </div>
        )}

        {tab === 'coupons' && (
          <div className="space-y-3">
            {coupons.map((c) => (
              <div key={c.id}>
                {editingId === c.id ? (
                  <div className="bg-zinc-900 border-2 border-yellow-600 p-6">
                    <h2 className="text-xl font-bold text-white mb-4">Editar Cupom</h2>
                    <CouponForm formData={formData} setFormData={setFormData} onSave={handleSave} onCancel={cancelEdit} />
                  </div>
                ) : (
                  <div className="bg-zinc-900 border border-zinc-800 p-4 hover:border-red-600 transition-colors" data-testid={`coupon-${c.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3">
                          <span className="text-white font-bold text-lg">{c.code}</span>
                          <span className={`text-[10px] px-2 py-0.5 font-bold uppercase ${c.ativo ? 'bg-green-600 text-white' : 'bg-zinc-700 text-zinc-400'}`}>{c.ativo ? 'Ativo' : 'Inativo'}</span>
                          <span className="text-[10px] px-2 py-0.5 bg-zinc-700 text-zinc-300 font-bold uppercase">{c.tipo === 'porcentagem' ? `${c.valor}%` : 'Frete Grátis'}</span>
                        </div>
                        <div className="flex gap-4 mt-1 text-xs text-zinc-500">
                          <span>Min: R$ {c.minCompra?.toFixed(2)}</span>
                          <span>Usos: {c.usosCount}/{c.maxUsos}</span>
                          {c.expiraEm && <span>Expira: {new Date(c.expiraEm).toLocaleDateString('pt-BR')}</span>}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => startEdit(c)} className="bg-yellow-600 hover:bg-yellow-700 text-white p-2"><Edit className="h-4 w-4" /></button>
                        <button onClick={() => handleDelete(c.id)} className="bg-red-600 hover:bg-red-700 text-white p-2"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
            {!loading && coupons.length === 0 && <p className="text-center text-zinc-500 py-16">Nenhum cupom criado ainda.</p>}
          </div>
        )}

        {tab === 'usage' && (
          <div className="bg-zinc-900 border border-zinc-800 overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-800"><th className="text-left text-zinc-400 font-bold text-xs uppercase tracking-wider p-4">Cupom</th><th className="text-left text-zinc-400 font-bold text-xs uppercase tracking-wider p-4">Subtotal</th><th className="text-left text-zinc-400 font-bold text-xs uppercase tracking-wider p-4">Data</th></tr></thead>
              <tbody>
                {usage.map((u, i) => (
                  <tr key={i} className="border-b border-zinc-800/50"><td className="text-white font-bold p-4">{u.code}</td><td className="text-zinc-400 p-4">R$ {u.subtotal?.toFixed(2)}</td><td className="text-zinc-500 p-4">{new Date(u.used_at).toLocaleString('pt-BR')}</td></tr>
                ))}
                {usage.length === 0 && <tr><td colSpan={3} className="text-center text-zinc-500 py-8">Nenhum uso registrado</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}

function CouponForm({ formData, setFormData, onSave, onCancel }) {
  const upd = (k, v) => setFormData(prev => ({ ...prev, [k]: v }));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div><label className="block text-zinc-400 text-xs font-bold uppercase mb-1">Código*</label><input type="text" value={formData.code} onChange={(e) => upd('code', e.target.value.toUpperCase())} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm focus:outline-none focus:border-red-500" data-testid="coupon-code-input" /></div>
        <div><label className="block text-zinc-400 text-xs font-bold uppercase mb-1">Tipo*</label><select value={formData.tipo} onChange={(e) => upd('tipo', e.target.value)} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm" data-testid="coupon-type-select"><option value="porcentagem">Porcentagem (%)</option><option value="frete_gratis">Frete Grátis</option></select></div>
        {formData.tipo === 'porcentagem' && <div><label className="block text-zinc-400 text-xs font-bold uppercase mb-1">Valor (%)</label><input type="number" step="1" value={formData.valor} onChange={(e) => upd('valor', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm" /></div>}
        <div><label className="block text-zinc-400 text-xs font-bold uppercase mb-1">Compra Mínima</label><input type="number" step="0.01" value={formData.minCompra} onChange={(e) => upd('minCompra', parseFloat(e.target.value) || 0)} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm" /></div>
        <div><label className="block text-zinc-400 text-xs font-bold uppercase mb-1">Máx. Usos</label><input type="number" value={formData.maxUsos} onChange={(e) => upd('maxUsos', parseInt(e.target.value) || 0)} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm" /></div>
        <div><label className="block text-zinc-400 text-xs font-bold uppercase mb-1">Expira em</label><input type="datetime-local" value={formData.expiraEm || ''} onChange={(e) => upd('expiraEm', e.target.value)} className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 text-white text-sm" /></div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={formData.ativo} onChange={(e) => upd('ativo', e.target.checked)} className="w-4 h-4 text-red-600 bg-zinc-800 border-zinc-700 rounded" /><span className="text-white text-sm font-bold">Ativo</span></label>
      <div className="flex gap-3">
        <button onClick={onSave} className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 uppercase tracking-wider text-sm flex items-center justify-center gap-2" data-testid="save-coupon-btn"><Save className="h-4 w-4" /> Salvar</button>
        <button onClick={onCancel} className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white font-bold py-3 uppercase tracking-wider text-sm flex items-center justify-center gap-2"><X className="h-4 w-4" /> Cancelar</button>
      </div>
    </div>
  );
}
